import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';

import { hrApi } from '@/api';

import { Button } from '@/components/ui/Button';

import { MultiDayCalendarPicker } from '@/components/ui/MultiDayCalendarPicker';

import { LeaveTypeSelect } from '@/components/selects';

import { Skeleton } from '@/components/ui/Skeleton';

import { HrLeaveReasonAttachmentFields } from '@/modules/hr/components/HrLeaveReasonAttachmentFields';

import { uploadLeaveAttachment } from '@/modules/hr/components/leaveAttachmentUtils';

import { hrExpandLeaveDays } from '@/modules/hr/components/hrCalendarUtils';

import { tierOptions } from '@/lib/query';

import type { HrLeaveRequest } from '@/types/hr';

import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';



interface Props {

  leaveId: number;

  essScope?: boolean;

  /** When true, allow edit even if leave.statusCode has not synced to RETURNED yet. */

  approvalReturned?: boolean;

  onSaved?: () => void;

  onCancel?: () => void;

}



function leaveToSelectedDates(leave: HrLeaveRequest): string[] {

  if (leave.selectedDates?.length) {

    return [...leave.selectedDates].sort();

  }

  if (leave.startDate === leave.endDate) {

    return [leave.startDate];

  }

  return hrExpandLeaveDays(leave.startDate, leave.endDate, leave.startDate, leave.endDate);

}



export function ReturnedLeaveEditPanel({

  leaveId,

  essScope = true,

  approvalReturned = false,

  onSaved,

  onCancel,

}: Props) {
  const { t } = useTranslation('workflow');

  const [leaveTypeId, setLeaveTypeId] = useState('');

  const [selectedDates, setSelectedDates] = useState<string[]>([]);

  const [leaveReason, setLeaveReason] = useState('');

  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);

  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);



  const { data: leave, isLoading } = useQuery({

    queryKey: ['hr', 'leave', essScope ? 'ess' : 'hr', leaveId],

    queryFn: () => (essScope ? hrApi.essGetLeave(leaveId) : hrApi.getLeave(leaveId)),

    enabled: leaveId > 0,

    ...tierOptions('operational'),

  });



  const calendarMode = useMemo(() => {

    const mode = leave?.durationMode ?? 'FULL_DAY';

    return mode === 'FULL_DAY' ? 'multiple' : 'single';

  }, [leave?.durationMode]);



  useEffect(() => {

    if (!leave) return;

    setLeaveTypeId(String(leave.leaveTypeId));

    setSelectedDates(leaveToSelectedDates(leave));

    setLeaveReason(leave.reason ?? '');

    setExistingAttachmentUrl(leave.attachmentUrl ?? null);

    setAttachmentFile(null);

  }, [leave]);



  const save = async () => {

    if (!leaveTypeId || selectedDates.length === 0) {

      notify.error(t('errors.leaveTypeAndAtLeastOneDayAre'));

      return;

    }

    if (!leaveReason.trim()) {

      notify.error(t('errors.leaveReasonIsRequired'));

      return;

    }

    setSaving(true);

    try {

      const sortedDates = [...selectedDates].sort();
      const uploadedUrl = attachmentFile ? await uploadLeaveAttachment(attachmentFile) : undefined;
      const resolvedAttachmentUrl =
        uploadedUrl !== undefined
          ? uploadedUrl
          : existingAttachmentUrl === null
            ? ''
            : existingAttachmentUrl ?? undefined;
      const body = {
        leaveTypeId: Number(leaveTypeId),
        selectedDates: sortedDates,
        reason: leaveReason.trim(),
        attachmentUrl: resolvedAttachmentUrl,

        durationMode: leave?.durationMode,

        halfDaySession: leave?.halfDaySession,

        leaveHours: leave?.leaveHours,

      };

      if (essScope) {

        await hrApi.essUpdateLeave(leaveId, {

          ...body,

          startDate: sortedDates[0],

          endDate: sortedDates[sortedDates.length - 1],

        });

      } else {

        await hrApi.updateLeave(leaveId, {

          employeeId: leave!.employeeId,

          ...body,

          startDate: sortedDates[0],

          endDate: sortedDates[sortedDates.length - 1],

        });

      }

      notify.t.success('toast.leaveUpdatedResubmitWhenReady', { ns: 'workflow' });

      onSaved?.();

    } catch (err) {

      showApiError(err, t('errors.failedToUpdateLeave'));

    } finally {

      setSaving(false);

    }

  };



  if (isLoading) {

    return <Skeleton className="h-40 w-full rounded-lg" />;

  }



  if (!leave) {

    return <p className="text-sm text-slate-500">Leave request not found.</p>;

  }



  const editable = approvalReturned || leave.statusCode === 'RETURNED';



  if (!editable) {

    return (

      <p className="text-sm text-slate-500">

        This leave is no longer returned for edits (status: {leave.statusCode}).

      </p>

    );

  }



  return (

    <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">

      <h5 className="text-sm font-medium text-slate-800 dark:text-slate-200">Edit leave request</h5>

      <LeaveTypeSelect

        value={leaveTypeId}

        onChange={setLeaveTypeId}

        required

        essScope={essScope}

        employeeId={leave.employeeId}

      />

      <MultiDayCalendarPicker

        label={calendarMode === 'single' ? 'Leave day' : 'Leave days'}

        value={selectedDates}

        onChange={setSelectedDates}

        mode={calendarMode}

        required

        hint={

          calendarMode === 'single'

            ? 'Select one day for half-day or hourly leave.'

            : 'Click or drag across days to select. Only the days you pick are included — gaps are kept.'

        }

      />

      <HrLeaveReasonAttachmentFields

        reason={leaveReason}

        onReasonChange={setLeaveReason}

        attachmentFile={attachmentFile}

        onAttachmentFileChange={setAttachmentFile}

        existingAttachmentUrl={existingAttachmentUrl}

        onClearExistingAttachment={() => setExistingAttachmentUrl(null)}

        uploading={saving}

      />

      <div className="flex flex-wrap gap-2">

        <Button size="sm" loading={saving} onClick={() => void save()}>

          Save changes

        </Button>

        {onCancel ? (

          <Button size="sm" variant="ghost" onClick={onCancel}>

            Cancel

          </Button>

        ) : null}

      </div>

    </div>

  );

}

